import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Flame, Coins, Terminal, Zap, TrendingUp } from "lucide-react";
import EmberField from "@/components/EmberField";
import DonationTicker from "@/components/DonationTicker";
import FeeCounter from "@/components/FeeCounter";

const CounterCard = ({ label, value, hint, icon: Icon, testId }) => (
  <div
    data-testid={testId}
    className="relative rounded-xl border border-[#3A2E1C] bg-[#141008] p-5 hover:border-[#FFAB00]/50 hover:shadow-[0_0_30px_rgba(255,87,34,0.15)] transition-all"
  >
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-widest text-stone-500">{label}</span>
      <Icon className="w-4 h-4 text-[#FFC940]" />
    </div>
    <div className="mt-3 font-display text-2xl sm:text-3xl font-bold ember-text">{value}</div>
    {hint && <div className="mt-1 text-xs text-stone-500">{hint}</div>}
  </div>
);

export default function Landing() {
  const [s, setS] = useState({
    total_dust_usd: 0, donation_count: 0, wallets_rescued: 0,
    quotrons_owned: 0, yield_distributed_usd: 0,
  });
  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("/stats/global");
        setS(r.data);
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden surface-grain scanlines">
        <EmberField count={40} />
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(255,171,0,0.22), transparent 70%)" }}
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-24 pb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FFAB00]/40 bg-[#141008]/80 px-3 py-1 text-xs mono text-stone-300">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFAB00] flicker" />
            quotron online · all 8 chains · donations open
          </div>
          <h1 className="mt-6 font-display text-6xl sm:text-8xl lg:text-9xl font-black leading-[0.95] tracking-tight max-w-5xl uppercase">
            you got rugged.<br />
            <span className="ember-text">welcome home<span className="caret" /></span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-stone-300 leading-relaxed">
            Donate your dead-coin dust from any chain. We cremate what's sellable,
            buy and hardwire <span className="text-[#FFD54F] font-semibold">Quotrons</span> on Robinhood Chain,
            and route the tokenized-stock yield (NVDA · TSLA · AAPL · SPY) back to $QUODUST holders.
            You get airdrops for showing up with your bags.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/donate">
              <Button
                data-testid="hero-cta-donate"
                size="lg"
                className="bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold h-12 px-6"
              >
                <Flame className="w-4 h-4 mr-2" /> cremate your dust
              </Button>
            </Link>
            <Link to="/how">
              <Button
                data-testid="hero-cta-flywheel"
                size="lg"
                variant="outline"
                className="h-12 px-6 border-[#3A2E1C] bg-transparent hover:bg-[#141008] text-stone-100"
              >
                see the flywheel <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a
              href="https://pons.exchange"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-stone-400 hover:text-[#FFC940] underline underline-offset-4"
              data-testid="hero-link-pons"
            >
              buy $QUODUST on Pons →
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-start gap-3" data-testid="hero-fee-row">
            <FeeCounter />
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <CounterCard testId="stat-dust-donated" label="Dust donated" value={fmtUSD(s.total_dust_usd)} hint={`${s.donation_count} tx cremated`} icon={Coins} />
            <CounterCard testId="stat-wallets-rescued" label="Wallets rescued" value={s.wallets_rescued.toLocaleString()} hint="unique survivors" icon={Terminal} />
            <CounterCard testId="stat-quotrons" label="Quotrons owned" value={s.quotrons_owned} hint="hardwired · Robinhood Chain" icon={Zap} />
            <CounterCard testId="stat-yield" label="Yield distributed" value={fmtUSD(s.yield_distributed_usd)} hint="to $QUODUST holders" icon={TrendingUp} />
          </div>
        </div>
        <DonationTicker />
      </section>

      {/* FEATURE TRIAD */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-4">
          {[
            { t: "bags welcome", d: "SAFEMOON. LUNC. BONKRUG. Your dust from Ethereum, Base, Arbitrum, Optimism, Polygon, BSC, Avalanche, Solana — all of it gets a dignified cremation here." },
            { t: "3% fee → quotrons", d: "Every $QUODUST trade on Pons pays a 3% fee. Those fees fund Quotrons terminal purchases on Robinhood Chain. Each hardwired terminal prints tokenized-stock yield (NVDA · TSLA · AAPL · SPY · GME · more)." },
            { t: "airdrops for the burned", d: "Every USD you donate counts toward your monthly $QUODUST allocation. Snapshot taken onchain · nothing to farm, nothing to game." },
          ].map((f, i) => (
            <div key={i} className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-6">
              <div className="text-xs uppercase tracking-widest text-[#FFAB00]">0{i + 1}</div>
              <div className="mt-2 font-display text-2xl">{f.t}</div>
              <p className="mt-2 text-sm text-stone-400 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
